import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowLeft, Clock, Users, MapPin, Calendar, CheckCircle, X } from 'lucide-react';

// Stop schemas (letter + slug + photo). Name/description come from translations.
const ONE_HOUR_STOPS = [
  { letter: 'A', slug: 'iLoveKefalonia', photo: '/assets/tour1-A-iloveke.jpg' },
  { letter: 'B', slug: 'ww2Memorial', photo: '/assets/tour1-B-ww2.jpg' },
  { letter: 'C', slug: 'stTheodoreLighthouse', photo: '/assets/tour1-C-lighthouse.jpg' },
  { letter: 'D', slug: 'argostoliSinkholes', photo: '/assets/tour1-D-sinkholes.jpg' },
  { letter: 'E', slug: 'centralSquare', photo: '/assets/tour1-E-central.jpg' },
  { letter: 'F', slug: 'koutavosLagoon', photo: '/assets/tour1-F-koutavos.jpg' },
  { letter: 'G', slug: 'deBossetBridge', photo: '/assets/tour1-G-debosset.jpg' },
  { letter: 'H', slug: 'carettaCarettaWatchpoint', photo: '/assets/tour1-H-caretta.jpg' }
];

const TWO_HOURS_STOPS = [
  { letter: 'A', slug: 'iLoveKefalonia', photo: '/assets/tour1-A-iloveke.jpg' },
  { letter: 'B', slug: 'argostoliSinkholes', photo: '/assets/tour1-D-sinkholes.jpg' },
  { letter: 'C', slug: 'stTheodoreLighthouse', photo: '/assets/tour1-C-lighthouse.jpg' },
  { letter: 'D', slug: 'ww2Memorial', photo: '/assets/tour1-B-ww2.jpg' },
  { letter: 'E', slug: 'makrisGialosBeach', photo: '/assets/tour2-E-makris.jpg' },
  { letter: 'F', slug: 'platisGialosBeach', photo: '/assets/tour2-F-platisgialos.jpg' },
  { letter: 'G', slug: 'koutavosLagoon', photo: '/assets/tour1-F-koutavos.jpg' },
  { letter: 'H', slug: 'deBossetBridge', photo: '/assets/tour1-G-debosset.jpg' },
  { letter: 'I', slug: 'carettaCarettaWatchpoint', photo: '/assets/tour1-H-caretta.jpg' },
  { letter: 'J', slug: 'centralSquare', photo: '/assets/tour1-E-central.jpg' }
];

const THREE_HOURS_STOPS = [
  { letter: 'A', slug: 'iLoveKefalonia', photo: '/assets/tour1-A-iloveke.jpg' },
  { letter: 'B', slug: 'argostoliSinkholes', photo: '/assets/tour1-D-sinkholes.jpg' },
  { letter: 'C', slug: 'stTheodoreLighthouse', photo: '/assets/tour1-C-lighthouse.jpg' },
  { letter: 'D', slug: 'ww2Memorial', photo: '/assets/tour1-B-ww2.jpg' },
  { letter: 'E', slug: 'makrisGialosBeach', photo: '/assets/tour2-E-makris.jpg' },
  { letter: 'F', slug: 'platisGialosBeach', photo: '/assets/tour2-F-platisgialos.jpg' },
  { letter: 'G', slug: 'gentiliniWinery', photo: '/assets/tour3-G-gentilini.jpg' },
  { letter: 'H', slug: 'koutavosLagoon', photo: '/assets/tour1-F-koutavos.jpg' },
  { letter: 'I', slug: 'deBossetBridge', photo: '/assets/tour1-G-debosset.jpg' },
  { letter: 'J', slug: 'carettaCarettaWatchpoint', photo: '/assets/tour1-H-caretta.jpg' },
  { letter: 'K', slug: 'centralSquare', photo: '/assets/tour1-E-central.jpg' }
];

const STOPS_BY_TOUR = {
  oneHour: ONE_HOUR_STOPS,
  twoHours: TWO_HOURS_STOPS,
  threeHours: THREE_HOURS_STOPS
};

// Pricing per tour: total or per-person cost based on group size
const TOUR_PRICING = {
  oneHour: {
    rows: [
      { passengers: 1, price: '€60',isTotal: true },
      { passengers: 2, price: '€40',isTotal: false },
      { passengers: 3, price: '€35',isTotal: false },
      { passengers: 4, price: '€30',isTotal: false }
    ]
  },
  twoHours: {
    rows: [
      { passengers: 1, price: '€100', isTotal: true },
      { passengers: 2, price: '€65', isTotal: false },
      { passengers: 3, price: '€50', isTotal: false },
      { passengers: 4, price: '€45', isTotal: false }
    ]
  },
  threeHours: {
    rows: [
      { passengers: 1, price: '€130', isTotal: true },
      { passengers: 2, price: '€100', isTotal: false },
      { passengers: 3, price: '€80', isTotal: false },
      { passengers: 4, price: '€75', isTotal: false }
    ],
    showWineryFootnote: true
  }
};

const TourDetailPage = () => {
  const { tourId } = useParams();
  const { t } = useLanguage();
  const [activeStop, setActiveStop] = useState(null); // for modal (holds {letter, slug, photo})
  const [hoveredStop, setHoveredStop] = useState(null); // letter only

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tourId]);

  // Lock body scroll while modal open
  useEffect(() => {
    if (activeStop) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [activeStop]);

  // Map tour IDs to tour data (styling + map URL + image)
  const tourDetails = {
    'tour-oneHour': {
      key: 'oneHour',
      icon: Clock,
      color: 'from-sky-400 to-sky-500',
      bgColor: 'bg-sky-50',
      iconColor: 'text-sky-600',
      buttonColor: 'bg-sky-500 hover:bg-sky-600',
      image: '/assets/tour1-hero.jpg',
      price: 'from €30 per person',
      mapUrl: 'https://www.google.com/maps/d/u/0/embed?mid=19aAGjdhRal-5xYhbw91csu-OAtZwKl0&ehbc=2E312F',
      hoursLabel: t.tourDetail.hoursOne
    },
    'tour-twoHours': {
      key: 'twoHours',
      icon: Users,
      color: 'from-emerald-400 to-emerald-500',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      buttonColor: 'bg-emerald-500 hover:bg-emerald-600',
      image: '/assets/tour2-hero.jpg',
      price: 'from €45 per person',
      mapUrl: 'https://www.google.com/maps/d/embed?mid=1gSBG6yzsRQmHqGhkbbHNZhrUqTulj7Q&ehbc=2E312F',
      hoursLabel: t.tourDetail.hoursTwo
    },
    'tour-threeHours': {
      key: 'threeHours',
      icon: MapPin,
      color: 'from-violet-400 to-violet-500',
      bgColor: 'bg-violet-50',
      iconColor: 'text-violet-600',
      buttonColor: 'bg-violet-500 hover:bg-violet-600',
      image: '/assets/tour3-hero.jpg',
      price: 'from €75 per person',
      mapUrl: 'https://www.google.com/maps/d/embed?mid=1pf2fmwq6Dl05cuc1yhuWJhVYopt1HyU&ehbc=2E312F',
      hoursLabel: t.tourDetail.hoursThree
    }
  };

  const tour = tourDetails[tourId];

  // If tour not found, redirect to home
  if (!tour) {
    return <Navigate to="/" replace />;
  }

  const tourData = t.tours[tour.key];
  const Icon = tour.icon;
  const stops = STOPS_BY_TOUR[tour.key] || [];
  // Sort by letter (A → ...)
  const stopsAlpha = [...stops].sort((a, b) => a.letter.localeCompare(b.letter));

  const includedItems = [
    t.tourDetail.included.driver,
    t.tourDetail.included.pickup,
    t.tourDetail.included.fees,
    t.tourDetail.included.water,
    t.tourDetail.included.photoOps,
    t.tourDetail.included.wifi,
    ...(tour.key === 'twoHours' ? [t.tourDetail.included.beachStop] : []),
    ...(tour.key === 'threeHours' ? [t.tourDetail.included.winery] : [])
  ];

  const activeStopName = activeStop ? t.stops[activeStop.slug]?.name : '';
  const activeStopDescription = activeStop ? t.stops[activeStop.slug]?.description : '';

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section with Image */}
      <div className="relative h-[60vh] overflow-hidden">
        <img 
          src={tour.image} 
          alt={tourData.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-transparent" />
        
        {/* Back Button */}
        <Link 
          to="/#tours"
          className="absolute top-8 left-8 flex items-center space-x-2 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-lg shadow-lg hover:bg-white transition-all group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-semibold">{t.tourDetail.backButton}</span>
        </Link>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="container mx-auto">
            <div className={`inline-flex items-center space-x-2 px-4 py-2 ${tour.bgColor} rounded-full mb-4`}>
              <Icon className={`w-5 h-5 ${tour.iconColor}`} />
              <span className={`${tour.iconColor} font-semibold text-sm uppercase tracking-wide`}>
                {tourData.duration || tour.hoursLabel}
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg">
              {tourData.title}
            </h1>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-white">
                <span className="text-2xl font-bold">{tour.price}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          
          {/* Description */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">{t.tourDetail.aboutThisTour}</h2>
            <p className="text-lg text-gray-700 leading-relaxed whitespace-pre-line">
              {tourData.description}
            </p>
          </div>

          {/* Map and Tour Stops Section */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">{t.tourDetail.tourRouteStops}</h2>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Map on the left */}
              <div className="order-2 lg:order-1">
                <div className="rounded-xl overflow-hidden shadow-md h-full min-h-[400px] lg:min-h-[640px]">
                  {tour.mapUrl ? (
                    <div className="relative w-full h-full overflow-hidden" style={{ height: '640px' }}>
                      <iframe 
                        src={tour.mapUrl}
                        width="100%" 
                        height="700"
                        style={{ border: 0, marginTop: '-60px', display: 'block' }}
                        allowFullScreen
                        loading="lazy"
                        title="Tour Route Map"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <div className="text-center p-8">
                        <MapPin className={`w-16 h-16 ${tour.iconColor} mx-auto mb-4`} />
                        <p className="text-gray-600 text-lg">Custom tour route map</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Stops list on the right */}
              <div className="order-1 lg:order-2">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t.tourDetail.keyStops}</h3>
                <div className="space-y-0.5">
                  {stopsAlpha.map((stop) => {
                    const stopName = t.stops[stop.slug]?.name || stop.slug;
                    const stopDescription = t.stops[stop.slug]?.description || '';
                    return (
                      <div
                        key={stop.letter}
                        className="relative flex items-start space-x-3 px-2 py-1.5 rounded-md hover:bg-gray-50 transition-colors cursor-pointer group"
                        onMouseEnter={() => setHoveredStop(stop.letter)}
                        onMouseLeave={() => setHoveredStop(null)}
                        onClick={() => stop.photo && setActiveStop(stop)}
                        data-testid={`tour-stop-${stop.letter}`}
                      >
                        <div className={`flex-shrink-0 w-9 h-9 ${tour.bgColor} rounded-full flex items-center justify-center`}>
                          <span className={`${tour.iconColor} font-bold text-sm`}>{stop.letter}</span>
                        </div>
                        <div className="flex-grow min-w-0">
                          <h4 className="font-semibold text-gray-900 text-sm leading-tight flex items-center gap-2">
                            {stopName}
                            {stop.photo && (
                              <span className="text-[10px] text-gray-400 group-hover:text-amber-600 transition-colors whitespace-nowrap">
                                {t.tourDetail.clickToEnlarge}
                              </span>
                            )}
                          </h4>
                          <p className="text-gray-600 text-xs leading-snug mt-0.5">{stopDescription}</p>
                        </div>

                        {/* Hover popover preview (desktop only) */}
                        {hoveredStop === stop.letter && stop.photo && (
                          <div
                            className="hidden md:block absolute left-full top-1/2 -translate-y-1/2 ml-4 z-30 pointer-events-none"
                            style={{ width: '320px' }}
                          >
                            <div className="bg-white rounded-lg shadow-2xl overflow-hidden ring-1 ring-black/5">
                              <img
                                src={stop.photo}
                                alt={stopName}
                                className="w-full block"
                                style={{ width: '320px', height: '240px', objectFit: 'cover' }}
                              />
                              <div className="px-3 py-2 text-xs text-gray-700 font-medium">
                                {stop.letter} — {stopName}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Tour Details Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            
            {/* Duration Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className={`p-3 ${tour.bgColor} rounded-lg`}>
                  <Clock className={`w-6 h-6 ${tour.iconColor}`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{t.tourDetail.duration}</h3>
              </div>
              <p className="text-gray-600 text-lg">{tourData.duration || tour.hoursLabel}</p>
            </div>

            {/* Group Size Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className={`p-3 ${tour.bgColor} rounded-lg`}>
                  <Users className={`w-6 h-6 ${tour.iconColor}`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{t.tourDetail.groupSize}</h3>
              </div>
              <p className="text-gray-600 text-lg">{t.tourDetail.upTo4}</p>
            </div>

            {/* Starting Point Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className={`p-3 ${tour.bgColor} rounded-lg`}>
                  <MapPin className={`w-6 h-6 ${tour.iconColor}`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{t.tourDetail.startingPoint}</h3>
              </div>
              <p className="text-gray-600 text-lg">{t.tourDetail.startingPointValue}</p>
            </div>

            {/* Availability Card */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className={`p-3 ${tour.bgColor} rounded-lg`}>
                  <Calendar className={`w-6 h-6 ${tour.iconColor}`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{t.tourDetail.availability}</h3>
              </div>
              <p className="text-gray-600 text-lg">{t.tourDetail.availabilityValue}</p>
            </div>
          </div>

          {/* Pricing Section */}
          {TOUR_PRICING[tour.key] && (
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">{t.tourDetail.pricing}</h2>
              <div className="overflow-hidden rounded-xl border border-gray-200">
                <table className="w-full">
                  <thead className={`${tour.bgColor}`}>
                    <tr>
                      <th className={`text-left px-5 py-3 text-sm font-semibold uppercase tracking-wide ${tour.iconColor}`}>
                        {t.tourDetail.passengersCol}
                      </th>
                      <th className={`text-right px-5 py-3 text-sm font-semibold uppercase tracking-wide ${tour.iconColor}`}>
                        {t.tourDetail.priceCol}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {TOUR_PRICING[tour.key].rows.map((row) => (
                      <tr
                        key={row.passengers}
                        className="border-t border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-5 py-4 text-gray-700 text-lg">
                          {row.passengers} {row.passengers === 1 ? t.tourDetail.personSingular : t.tourDetail.personPlural}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <span className="text-2xl font-bold text-gray-900">{row.price}</span>
                          <span className="text-sm text-gray-500 ml-1">
                            {row.isTotal ? t.tourDetail.totalLabel : t.tourDetail.perPersonUnit}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {TOUR_PRICING[tour.key].showWineryFootnote && (
                <p className="text-sm text-gray-500 italic mt-4">
                  {t.tourDetail.wineryFootnote}
                </p>
              )}
            </div>
          )}

          {/* What's Included */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">{t.tourDetail.whatsIncluded}</h2>
            <ul className="space-y-4">
              {includedItems.map((item) => (
                <li key={item} className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA Section */}
          <div className={`rounded-2xl ${tour.bgColor} p-8 text-center`}>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {t.tourDetail.readyToBook}
            </h3>
            <p className="text-gray-700 mb-6 text-lg">
              {t.tourDetail.contactPrompt}
            </p>
            <Link
              to={`/#contact?tour=${tour.key}`}
              className={`inline-flex items-center space-x-2 px-8 py-4 ${tour.buttonColor} text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl`}
            >
              <span>{t.tourDetail.contactUs}</span>
              <ArrowLeft className="w-5 h-5 rotate-180" />
            </Link>

            {/* Viber / WhatsApp quick-contact */}
            <p className="text-gray-700 mt-6 text-base flex flex-wrap items-center justify-center gap-3">
              <span>{t.tourDetail.reachUs}</span>
              <a
                href="viber://chat?number=%2B306937158874"
                target="_blank"
                rel="noopener noreferrer"
                title="Contact via Viber"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#7360F2] text-white hover:scale-110 transition-transform shadow-md"
                data-testid={`tour-${tour.key}-viber-link`}
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                  <path d="M11.4 0C9.473.028 5.333.344 3.02 2.467 1.302 4.187.696 6.7.633 9.817.57 12.933.488 18.617 6.55 20.23h.01L6.55 23s-.053.17.114.233c.166.062.262-.107.262-.107l3.09-3.927s.91.144 1.685.233c.465.052.95.075 1.44.075 1.92 0 5.99-.317 8.3-2.44 1.72-1.72 2.33-4.23 2.39-7.35.06-3.11.14-8.8-5.92-9.77-.34-.05-3.27-.11-5.53-.05z"/>
                </svg>
              </a>
              <a
                href="https://wa.me/306937158874"
                target="_blank"
                rel="noopener noreferrer"
                title="Contact via WhatsApp"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#25D366] text-white hover:scale-110 transition-transform shadow-md"
                data-testid={`tour-${tour.key}-whatsapp-link`}
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Stop Photo Modal */}
      {activeStop && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setActiveStop(null)}
          data-testid="stop-photo-modal"
        >
          <button
            onClick={() => setActiveStop(null)}
            className="absolute top-4 right-4 text-white hover:text-amber-400 transition-colors p-2 rounded-full bg-black/40 hover:bg-black/60"
            aria-label="Close"
            data-testid="stop-photo-modal-close"
          >
            <X className="w-7 h-7" />
          </button>
          <div
            className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={activeStop.photo}
              alt={activeStopName}
              className="rounded-lg shadow-2xl bg-black"
              style={{ width: 'min(800px, 90vw)', height: 'min(600px, 67.5vw)', objectFit: 'cover' }}
            />
            <div className="mt-4 text-center text-white">
              <div className="text-sm uppercase tracking-widest text-amber-400 mb-1">
                {activeStop.letter}
              </div>
              <div className="text-2xl font-bold">{activeStopName}</div>
              <p className="text-gray-300 text-sm mt-2 max-w-xl">{activeStopDescription}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TourDetailPage;
